import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  OutlinedInput,
  RadioGroup,
  Radio,
  FormLabel,
} from '@mui/material';
import AlertItem from '../../Components/AlertItem';
import {
  ArrowBack as ArrowBackIcon,
  PlayArrow as PlayArrowIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LineChart } from '@mui/x-charts/LineChart';
import dayjs, { Dayjs } from 'dayjs';
import { useApiGet } from '../../utils/httpClientTs';
import {
  getPEOReportMetadata,
  executePEOReport,
  exportPEOReportEmails,
  downloadPEOReportPDF,
  enqueuePEOReportAsync,
  getPEOReportExecutionStatus,
} from '../../endpoints/peoReportsTs';
import {
  ReportMetadata,
  ExecuteReportRequest,
  ExecuteReportResponse,
  BulkStatementSummary,
  REPORT_ID_ACTIVE_INACTIVE_CHAPTER_MEMBERS,
  REPORT_ID_MEMBERSHIP_YEAR_FINAL,
  REPORT_ID_CHAPTER_TRANSACTION_LOG,
  REPORT_ID_CHAPTER_MEMBERSHIP_HISTORY,
  REPORT_ID_CHAPTER_LISTING,
  REPORT_ID_CHAPTER_HEALTH,
  REPORT_ID_INCREASE_DECREASE_BY_CHAPTER,
  REPORT_ID_CHAPTER_STAR,
  REPORT_ID_MEMBERSHIP_YOY,
  REPORT_ID_INTERNATIONAL_REP_OFFICERS_CONFERENCE,
  REPORT_ID_MEMBERSHIP_GRAPHS,
  REPORT_ID_ANNUAL_REPORT_OF_MEMBERSHIP,
  REPORT_ID_ANNUAL_REPORT_OF_MEMBERSHIP_DISBANDED_CHAPTERS,
  REPORT_ID_FUNDS_BY_SPD,
  REPORT_ID_FOUNDATION_FUNDS_ACTIVITY_STATEMENTS,
  REPORT_ID_LOCAL_CHAPTER_OFFICER_ELECTION,
  REPORT_ID_LEADERSHIP_LISTING,
  REPORT_ID_MEMORIAL_REPORT,
  REPORT_ID_CLASS_REPORT,
  REPORT_ID_YEARS_OF_MEMBERSHIP_SPD,
  REPORT_ID_RESOURCE_STATISTICS,
  REPORT_ID_IRS_LC_FULL_SUMMARY,
  REPORT_ID_IRS_RG_FULL_SUMMARY,
} from '../../types/peoReports';
import ClientSideTable, {
  ClientSideTableColumn,
} from '../../Components/ClientSideTable';
import ExportButton from '../../Components/ExportButton';
import ChapterSelector from '../../Components/ChapterSelector';
import SPDSelector from '../../Components/SPDSelector';
import MemberActiveFilter from '../../Components/MemberActiveFilter';
import LocalChapterRolesFilter from '../../Components/LocalChapterRolesFilter';
import LeadershipPositionGroupsFilter from '../../Components/LeadershipPositionGroupsFilter';
import LeadershipPositionsFilter from '../../Components/LeadershipPositionsFilter';
import YearsAgoFilter from '../../Components/YearsAgoFilter';
import { getCategories, getSubCategories } from '../../endpoints/documents';
import type {
  DocumentCategory,
  DocumentSubCategory,
} from '../../types/document';

/** PDF is only available from parameters (no Run Report); results Export is not shown until JSON runs elsewhere. */
const PDF_DOWNLOAD_FROM_PARAMETERS_ONLY_IDS = new Set<string>([
  REPORT_ID_INTERNATIONAL_REP_OFFICERS_CONFERENCE,
  REPORT_ID_MEMBERSHIP_GRAPHS,
  REPORT_ID_ANNUAL_REPORT_OF_MEMBERSHIP,
  REPORT_ID_ANNUAL_REPORT_OF_MEMBERSHIP_DISBANDED_CHAPTERS,
  REPORT_ID_FUNDS_BY_SPD,
]);

const ASYNC_PDF_REPORT_IDS = new Set<string>([
  REPORT_ID_FOUNDATION_FUNDS_ACTIVITY_STATEMENTS,
]);

const getCurrentFiscalYearEnding = () => {
  const now = new Date();
  return now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
};

/** Reports that support `format=pdf` / handleDownloadPDF (shown in results Download menu when applicable). */
function reportSupportsPdfExport(reportId: string | undefined): boolean {
  if (!reportId) return false;
  if (PDF_DOWNLOAD_FROM_PARAMETERS_ONLY_IDS.has(reportId)) return true;
  return (
    reportId === REPORT_ID_ACTIVE_INACTIVE_CHAPTER_MEMBERS ||
    reportId === REPORT_ID_MEMBERSHIP_YEAR_FINAL ||
    reportId === REPORT_ID_CHAPTER_TRANSACTION_LOG ||
    reportId === REPORT_ID_CHAPTER_MEMBERSHIP_HISTORY ||
    reportId === REPORT_ID_CHAPTER_LISTING ||
    reportId === REPORT_ID_CHAPTER_HEALTH ||
    reportId === REPORT_ID_INCREASE_DECREASE_BY_CHAPTER ||
    reportId === REPORT_ID_CHAPTER_STAR ||
    reportId === REPORT_ID_MEMBERSHIP_YOY ||
    reportId === REPORT_ID_YEARS_OF_MEMBERSHIP_SPD ||
    reportId === REPORT_ID_LEADERSHIP_LISTING
  );
}

const CTL_TRANSACTION_SLUGS = [
  'transfer_out',
  'initiation',
  'reinstatement',
  'transfer_in',
  'death',
  'inactive',
] as const;

const CTL_TRANSACTION_LABELS: Record<
  (typeof CTL_TRANSACTION_SLUGS)[number],
  string
> = {
  transfer_out: 'Transfer Out',
  initiation: 'Initiation',
  reinstatement: 'Reinstatement',
  transfer_in: 'Transfer In',
  death: 'Death',
  inactive: 'Inactive',
};

function ctlSelectedSlugs(transactionTypes: string | undefined): string[] {
  const raw = (transactionTypes ?? '').trim();
  if (!raw) return [...CTL_TRANSACTION_SLUGS];
  const parts = raw
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  const allowed = new Set(CTL_TRANSACTION_SLUGS);
  const picked = parts.filter((s) =>
    allowed.has(s as (typeof CTL_TRANSACTION_SLUGS)[number])
  );
  return picked.length ? picked : [...CTL_TRANSACTION_SLUGS];
}

function stripChapterTransactionLogParams(
  p: ExecuteReportRequest
): ExecuteReportRequest {
  const next = { ...p };
  const sel = ctlSelectedSlugs(p.transaction_types);
  if (sel.length === CTL_TRANSACTION_SLUGS.length) {
    delete next.transaction_types;
  } else {
    next.transaction_types = sel.join(',');
  }
  if (!next.include_member_contact) {
    delete next.include_member_contact;
  }
  return next;
}

function omitUnsetIRSFullSummaryYear(
  reportId: string,
  p: ExecuteReportRequest
): ExecuteReportRequest {
  if (
    reportId !== REPORT_ID_IRS_LC_FULL_SUMMARY &&
    reportId !== REPORT_ID_IRS_RG_FULL_SUMMARY
  ) {
    return p;
  }
  const next = { ...p };
  if (
    next.year === undefined ||
    next.year === null ||
    !Number.isFinite(next.year) ||
    next.year <= 0
  ) {
    delete next.year;
  }
  return next;
}

function normalizeScopeParams(p: ExecuteReportRequest): ExecuteReportRequest {
  const next = { ...p };
  const local = (next.local_chapter_id ?? '').trim();
  const spd = (next.spd_chapter_id ?? '').trim();
  if (local) {
    next.local_chapter_id = local;
    delete next.spd_chapter_id;
    return next;
  }
  if (spd) {
    next.spd_chapter_id = spd;
    delete next.local_chapter_id;
    return next;
  }
  delete next.local_chapter_id;
  delete next.spd_chapter_id;
  return next;
}

type MembershipYoyMode = 'full_year' | 'as_of_date';

function stripMembershipYoYParams(
  mode: MembershipYoyMode,
  p: ExecuteReportRequest
): ExecuteReportRequest {
  const next = { ...p };
  if (mode === 'full_year') {
    delete next.as_of_date;
  } else {
    delete next.membership_year;
  }
  return next;
}

/** sessionStorage: skip Leadership Listing "groups + positions" confirmation until tab closes */
const LEADERSHIP_UNION_CONFIRM_SKIP_SESSION_KEY =
  'peo_reports_leadership_groups_positions_union_confirm_skip';

function hasLeadershipBothGroupAndPositionSelections(
  fv: ExecuteReportRequest
): boolean {
  const pg = (fv.position_group_type_ids ?? '').trim();
  const pr = (fv.position_role_ids ?? '').trim();
  return pg !== '' && pr !== '';
}

const PEOReportDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [filterValues, setFilterValues] = useState<ExecuteReportRequest>({});
  const [membershipYoyMode, setMembershipYoyMode] =
    useState<MembershipYoyMode>('full_year');
  const [reportData, setReportData] = useState<ExecuteReportResponse | null>(
    null
  );
  const [isExecuting, setIsExecuting] = useState(false);
  const [isPdfDownloading, setIsPdfDownloading] = useState(false);
  const [executionError, setExecutionError] = useState<string | null>(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailData, setEmailData] = useState<string>('');
  const [emailCount, setEmailCount] = useState<number>(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [leadershipUnionDialogOpen, setLeadershipUnionDialogOpen] =
    useState(false);
  const [dontAskLeadershipUnionAgain, setDontAskLeadershipUnionAgain] =
    useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<
    Record<string, boolean>
  >({});
  const [libraryCategories, setLibraryCategories] = useState<
    DocumentCategory[]
  >([]);
  const [librarySubCategories, setLibrarySubCategories] = useState<
    DocumentSubCategory[]
  >([]);
  const [asyncTaskLogId, setAsyncTaskLogId] = useState<string | null>(null);
  const [asyncStatus, setAsyncStatus] = useState<string | null>(null);
  const [asyncSummary, setAsyncSummary] = useState<BulkStatementSummary | null>(
    null
  );
  const [asyncDownloadURL, setAsyncDownloadURL] = useState<string | null>(null);
  const [asyncFilename, setAsyncFilename] = useState<string | null>(null);
  const [asyncError, setAsyncError] = useState<string | null>(null);
  const [isAsyncGenerating, setIsAsyncGenerating] = useState(false);

  const {
    data: metadata,
    isLoading,
    error,
  } = useApiGet<ReportMetadata>(
    ['peo-report-metadata', id],
    () => getPEOReportMetadata(id!),
    {
      enabled: !!id,
      retry: 1,
    }
  );

  const hasEmailColumn =
    metadata?.columns?.some((col) => col.type === 'email') || false;

  // Initialize column visibility from metadata
  useEffect(() => {
    if (metadata?.columns) {
      const initialVisibility: Record<string, boolean> = {};
      metadata.columns.forEach((col) => {
        initialVisibility[col.field] = col.visible;
      });
      setColumnVisibilityModel(initialVisibility);
    }
  }, [metadata]);

  useEffect(() => {
    if (id !== REPORT_ID_RESOURCE_STATISTICS) return;
    let cancelled = false;
    void (async () => {
      try {
        const [cats, subs] = await Promise.all([
          getCategories(),
          getSubCategories(),
        ]);
        if (!cancelled) {
          setLibraryCategories(cats);
          setLibrarySubCategories(subs);
        }
      } catch (e) {
        console.error('Failed to load library categories for report', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Current membership year (March 1–March 1): year of the start date
  const getCurrentMembershipYear = () => {
    const now = new Date();
    return now.getMonth() >= 2 ? now.getFullYear() : now.getFullYear() - 1;
  };

  // Initialize filter values from parameter defaults (only when current value is undefined)
  useEffect(() => {
    if (!metadata?.parameters?.length) return;
    setFilterValues((prev) => {
      let next = prev;
      for (const param of metadata.parameters) {
        if (param.default_value == null || param.default_value === '') continue;
        const key = param.name as keyof ExecuteReportRequest;
        const current = prev[key];
        if (current !== undefined && current !== '') continue;
        const value =
          param.type === 'number'
            ? Number(param.default_value)
            : param.default_value;
        if (next === prev) next = { ...prev };
        (next as Record<string, unknown>)[param.name] = value;
      }
      return next;
    });
  }, [metadata]);

  // Default membership_year for Membership Year Final report when undefined
  useEffect(() => {
    if (!id || id !== REPORT_ID_MEMBERSHIP_YEAR_FINAL || !metadata) return;
    setFilterValues((prev) => {
      if (prev.membership_year != null) return prev;
      return { ...prev, membership_year: getCurrentMembershipYear() };
    });
  }, [id, metadata]);

  // Default membership_year for Chapter Listing when undefined
  useEffect(() => {
    if (!id || id !== REPORT_ID_CHAPTER_LISTING || !metadata) return;
    setFilterValues((prev) => {
      if (prev.membership_year != null) return prev;
      return { ...prev, membership_year: getCurrentMembershipYear() };
    });
  }, [id, metadata]);

  // Default membership_year for Annual Report of Membership (and Disbanded Chapters) when undefined
  useEffect(() => {
    if (
      !id ||
      (id !== REPORT_ID_ANNUAL_REPORT_OF_MEMBERSHIP &&
        id !== REPORT_ID_ANNUAL_REPORT_OF_MEMBERSHIP_DISBANDED_CHAPTERS) ||
      !metadata
    )
      return;
    setFilterValues((prev) => {
      if (prev.membership_year != null) return prev;
      return { ...prev, membership_year: getCurrentMembershipYear() };
    });
  }, [id, metadata]);

  useEffect(() => {
    if (!id || id !== REPORT_ID_YEARS_OF_MEMBERSHIP_SPD || !metadata) return;
    setFilterValues((prev) => {
      if (prev.membership_year_target) return prev;
      return { ...prev, membership_year_target: 'current' };
    });
  }, [id, metadata]);

  useEffect(() => {
    if (id === REPORT_ID_MEMBERSHIP_YOY) {
      setMembershipYoyMode('full_year');
    }
  }, [id]);

  useEffect(() => {
    if (!id || id !== REPORT_ID_MEMBERSHIP_YOY || !metadata) return;
    if (membershipYoyMode !== 'full_year') return;
    setFilterValues((prev) => {
      if (prev.membership_year != null) return prev;
      return { ...prev, membership_year: getCurrentMembershipYear() };
    });
  }, [id, metadata, membershipYoyMode]);

  // Default membership year for IRS LC/RG full summary (API expects ending calendar year)
  useEffect(() => {
    if (
      !id ||
      (id !== REPORT_ID_IRS_LC_FULL_SUMMARY &&
        id !== REPORT_ID_IRS_RG_FULL_SUMMARY) ||
      !metadata
    )
      return;
    setFilterValues((prev) => {
      if (prev.year != null) return prev;
      return { ...prev, year: getCurrentMembershipYear() + 1 };
    });
  }, [id, metadata]);

  // Officer Election Reporting: default officer type + completion filter
  useEffect(() => {
    if (!id || id !== REPORT_ID_LOCAL_CHAPTER_OFFICER_ELECTION || !metadata)
      return;
    setFilterValues((prev) => {
      const ot = (prev.officer_election_type ?? '').trim();
      const cf = (prev.election_completion_filter ?? '').trim();
      if (ot !== '' && cf !== '') return prev;
      return {
        ...prev,
        ...(ot === '' ? { officer_election_type: 'local_chapter' } : {}),
        ...(cf === '' ? { election_completion_filter: 'all' } : {}),
      };
    });
  }, [id, metadata]);

  useEffect(() => {
    if (
      !id ||
      id !== REPORT_ID_FOUNDATION_FUNDS_ACTIVITY_STATEMENTS ||
      !metadata
    )
      return;
    setFilterValues((prev) => {
      if (prev.year != null) return prev;
      return { ...prev, year: getCurrentFiscalYearEnding() };
    });
  }, [id, metadata]);

  useEffect(() => {
    if (!asyncTaskLogId) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | undefined;

    const poll = async () => {
      try {
        const res = await getPEOReportExecutionStatus(asyncTaskLogId);
        if (cancelled) return;
        setAsyncStatus(res.status);
        if (res.summary) setAsyncSummary(res.summary);
        if (res.status === 'completed') {
          setIsAsyncGenerating(false);
          if (res.download_url) setAsyncDownloadURL(res.download_url);
          if (res.filename) setAsyncFilename(res.filename);
          if (timer) clearInterval(timer);
        } else if (res.status === 'error-go' || res.status === 'error') {
          setIsAsyncGenerating(false);
          setAsyncError(res.message || 'PDF generation failed');
          if (timer) clearInterval(timer);
        }
      } catch (e: any) {
        if (cancelled) return;
        setIsAsyncGenerating(false);
        setAsyncError(e.message || 'Failed to check generation status');
        if (timer) clearInterval(timer);
      }
    };

    poll();
    timer = setInterval(poll, 2500);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [asyncTaskLogId]);

  const handleFilterChange = (field: string, value: any) => {
    setFilterValues((prev) => ({ ...prev, [field]: value }));
  };

  const isRequiredFieldMissing = () => {
    if (!metadata) return true;
    if (id === REPORT_ID_CLASS_REPORT) return false; // No filters; always allow run
    if (id === REPORT_ID_LEADERSHIP_LISTING) {
      const orgWide = Boolean(filterValues.organization_wide);
      const hasScope =
        orgWide ||
        (filterValues.local_chapter_id ?? '').trim() !== '' ||
        (filterValues.spd_chapter_id ?? '').trim() !== '';
      const hasPositions =
        (filterValues.position_group_type_ids ?? '').trim() !== '' ||
        (filterValues.position_role_ids ?? '').trim() !== '';
      if (!hasScope || !hasPositions) return true;
    }
    if (id === REPORT_ID_MEMORIAL_REPORT) {
      const hasSpd = (filterValues.spd_chapter_id ?? '').trim() !== '';
      const hasStart = (filterValues.start_date ?? '').trim() !== '';
      const hasEnd = (filterValues.end_date ?? '').trim() !== '';
      const hasMembers = (filterValues.members ?? '').trim() !== '';
      if (!hasSpd || !hasStart || !hasEnd || !hasMembers) return true;
    }
    if (id === REPORT_ID_RESOURCE_STATISTICS) {
      const hasStart = (filterValues.start_date ?? '').trim() !== '';
      const hasEnd = (filterValues.end_date ?? '').trim() !== '';
      if (!hasStart || !hasEnd) return true;
      const startD = dayjs(filterValues.start_date);
      const endD = dayjs(filterValues.end_date);
      if (startD.isValid() && endD.isValid() && endD.isBefore(startD, 'day')) {
        return true;
      }
    }
    if (id === REPORT_ID_YEARS_OF_MEMBERSHIP_SPD) {
      const hasScope =
        (filterValues.spd_chapter_id ?? '').trim() !== '' ||
        (filterValues.local_chapter_id ?? '').trim() !== '';
      const hasMy =
        filterValues.membership_year_target === 'current' ||
        filterValues.membership_year_target === 'next';
      const hasAnn = (filterValues.anniversary_years ?? '').trim() !== '';
      if (!hasScope || !hasMy || !hasAnn) return true;
    }
    if (id === REPORT_ID_MEMBERSHIP_YOY) {
      if (membershipYoyMode === 'full_year') {
        const y = filterValues.membership_year;
        if (y == null || !Number.isFinite(y) || y < 2010) return true;
        return false;
      }
      const d = (filterValues.as_of_date ?? '').trim();
      if (!d) return true;
      const parsed = dayjs(d, 'YYYY-MM-DD', true);
      if (!parsed.isValid()) return true;
      if (parsed.isAfter(dayjs(), 'day')) return true;
      return false;
    }
    if (id === REPORT_ID_FOUNDATION_FUNDS_ACTIVITY_STATEMENTS) {
      const hasType = (filterValues.fund_type_id ?? '').trim() !== '';
      const y = filterValues.year;
      if (!hasType || y == null || !Number.isFinite(y) || y < 2010) return true;
      return false;
    }
    return metadata.parameters.some((param) => {
      if (!param.required) return false;
      const value = filterValues[param.name as keyof ExecuteReportRequest];
      return !value || value === '';
    });
  };

  const runReportExecution = async () => {
    if (!id) return;

    setIsExecuting(true);
    setExecutionError(null);

    try {
      let params =
        id === REPORT_ID_ACTIVE_INACTIVE_CHAPTER_MEMBERS &&
        filterValues.years_ago_inactive === undefined
          ? { ...filterValues, years_ago_inactive: 10 }
          : filterValues;
      if (id === REPORT_ID_CHAPTER_TRANSACTION_LOG) {
        params = stripChapterTransactionLogParams(params);
      }
      if (
        (id === REPORT_ID_CHAPTER_LISTING ||
          id === REPORT_ID_INCREASE_DECREASE_BY_CHAPTER) &&
        (params.chapter_status == null || params.chapter_status === '')
      ) {
        params = { ...params, chapter_status: 'active' };
      }
      params = omitUnsetIRSFullSummaryYear(id, params);
      params = normalizeScopeParams(params);
      if (id === REPORT_ID_MEMBERSHIP_YOY) {
        params = stripMembershipYoYParams(membershipYoyMode, params);
      }
      const response = await executePEOReport(id, {
        ...params,
        format: 'json',
      });
      setReportData(response);
      if (id === REPORT_ID_CHAPTER_TRANSACTION_LOG) {
        const showContact = !!filterValues.include_member_contact;
        setColumnVisibilityModel((prev) => ({
          ...prev,
          email: showContact,
          home_phone: showContact,
          cell_phone: showContact,
          address1: showContact,
          address2: showContact,
          city: showContact,
          state_name: showContact,
          postal_code: showContact,
        }));
      }
    } catch (err: any) {
      console.error('Report execution failed:', err);
      setExecutionError(
        err.message || 'Failed to execute report. Please try again.'
      );
    } finally {
      setIsExecuting(false);
    }
  };

  const handleExecuteReport = async () => {
    if (!id) return;

    const skipUnionConfirm =
      typeof sessionStorage !== 'undefined' &&
      sessionStorage.getItem(LEADERSHIP_UNION_CONFIRM_SKIP_SESSION_KEY) === '1';

    if (
      id === REPORT_ID_LEADERSHIP_LISTING &&
      hasLeadershipBothGroupAndPositionSelections(filterValues) &&
      !skipUnionConfirm
    ) {
      setDontAskLeadershipUnionAgain(false);
      setLeadershipUnionDialogOpen(true);
      return;
    }

    await runReportExecution();
  };

  const handleLeadershipUnionDialogCancel = () => {
    setLeadershipUnionDialogOpen(false);
  };

  const handleLeadershipUnionDialogConfirm = async () => {
    if (dontAskLeadershipUnionAgain && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(LEADERSHIP_UNION_CONFIRM_SKIP_SESSION_KEY, '1');
    }
    setLeadershipUnionDialogOpen(false);
    await runReportExecution();
  };

  const handleEmailExport = async () => {
    if (!id) return;

    try {
      let params =
        id === REPORT_ID_ACTIVE_INACTIVE_CHAPTER_MEMBERS &&
        filterValues.years_ago_inactive === undefined
          ? { ...filterValues, years_ago_inactive: 10 }
          : filterValues;
      if (id === REPORT_ID_CHAPTER_TRANSACTION_LOG) {
        params = stripChapterTransactionLogParams(params);
      }
      if (
        (id === REPORT_ID_CHAPTER_LISTING ||
          id === REPORT_ID_INCREASE_DECREASE_BY_CHAPTER) &&
        (params.chapter_status == null || params.chapter_status === '')
      ) {
        params = { ...params, chapter_status: 'active' };
      }
      params = omitUnsetIRSFullSummaryYear(id, params);
      params = normalizeScopeParams(params);
      if (id === REPORT_ID_MEMBERSHIP_YOY) {
        params = stripMembershipYoYParams(membershipYoyMode, params);
      }
      const response = await exportPEOReportEmails(id, params);
      setEmailData(response.emails);
      setEmailCount(response.count);
      setEmailDialogOpen(true);
    } catch (err: any) {
      console.error('Email export failed:', err);
      setSnackbarMessage('Failed to export emails. Please try again.');
      setSnackbarOpen(true);
    }
  };

  const handleDownloadPDF = async () => {
    if (!id) return;

    setIsPdfDownloading(true);
    try {
      let params =
        id === REPORT_ID_ACTIVE_INACTIVE_CHAPTER_MEMBERS &&
        filterValues.years_ago_inactive === undefined
          ? { ...filterValues, years_ago_inactive: 10 }
          : filterValues;
      if (id === REPORT_ID_CHAPTER_TRANSACTION_LOG) {
        params = stripChapterTransactionLogParams(params);
      }
      if (
        (id === REPORT_ID_CHAPTER_LISTING ||
          id === REPORT_ID_INCREASE_DECREASE_BY_CHAPTER) &&
        (params.chapter_status == null || params.chapter_status === '')
      ) {
        params = { ...params, chapter_status: 'active' };
      }
      params = omitUnsetIRSFullSummaryYear(id, params);
      params = normalizeScopeParams(params);
      if (id === REPORT_ID_MEMBERSHIP_YOY) {
        params = stripMembershipYoYParams(membershipYoyMode, params);
      }
      const blob = await downloadPEOReportPDF(id, params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download =
        id === REPORT_ID_MEMBERSHIP_YEAR_FINAL
          ? 'membership-year-final.pdf'
          : id === REPORT_ID_CHAPTER_TRANSACTION_LOG
            ? 'chapter-transaction-log.pdf'
            : id === REPORT_ID_CHAPTER_MEMBERSHIP_HISTORY
              ? 'chapter-membership-history.pdf'
              : id === REPORT_ID_CHAPTER_LISTING
                ? 'chapter-listing.pdf'
                : id === REPORT_ID_CHAPTER_HEALTH
                  ? 'chapter-health.pdf'
                  : id === REPORT_ID_INCREASE_DECREASE_BY_CHAPTER
                    ? 'increase-decrease-by-chapter.pdf'
                    : id === REPORT_ID_CHAPTER_STAR
                      ? 'chapter-star-report.pdf'
                      : id === REPORT_ID_MEMBERSHIP_YOY
                        ? 'membership-yoy.pdf'
                        : id === REPORT_ID_YEARS_OF_MEMBERSHIP_SPD
                          ? 'years-of-membership-spd.pdf'
                          : id ===
                              REPORT_ID_INTERNATIONAL_REP_OFFICERS_CONFERENCE
                            ? 'international-rep-officers-conference.pdf'
                            : id === REPORT_ID_MEMBERSHIP_GRAPHS
                              ? 'membership-graphs.pdf'
                              : id === REPORT_ID_ANNUAL_REPORT_OF_MEMBERSHIP
                                ? 'annual-report-of-membership.pdf'
                                : id ===
                                    REPORT_ID_ANNUAL_REPORT_OF_MEMBERSHIP_DISBANDED_CHAPTERS
                                  ? 'annual-report-of-membership-disbanded-chapters.pdf'
                                  : id === REPORT_ID_FUNDS_BY_SPD
                                    ? 'funds-by-spd.pdf'
                                    : id === REPORT_ID_LEADERSHIP_LISTING
                                      ? 'leadership-listing.pdf'
                                      : 'active-inactive-chapter-members.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSnackbarMessage('PDF downloaded.');
      setSnackbarOpen(true);
    } catch (err: any) {
      console.error('PDF download failed:', err);
      setSnackbarMessage(
        err.message || 'Failed to download PDF. Please try again.'
      );
      setSnackbarOpen(true);
    } finally {
      setIsPdfDownloading(false);
    }
  };

  const handleGenerateAsyncPDF = async () => {
    if (!id || !ASYNC_PDF_REPORT_IDS.has(id)) return;

    setAsyncTaskLogId(null);
    setAsyncStatus(null);
    setAsyncSummary(null);
    setAsyncDownloadURL(null);
    setAsyncFilename(null);
    setAsyncError(null);
    setIsAsyncGenerating(true);
    setAsyncStatus('processing');

    try {
      const res = await enqueuePEOReportAsync(id, {
        fund_type_id: filterValues.fund_type_id,
        year: filterValues.year,
      });
      setAsyncTaskLogId(res.task_log_id);
    } catch (err: any) {
      setIsAsyncGenerating(false);
      setAsyncError(
        err.message || 'Failed to start PDF generation. Please try again.'
      );
    }
  };

  const handleCopyEmails = () => {
    navigator.clipboard.writeText(emailData);
    setSnackbarMessage('Email addresses copied to clipboard!');
    setSnackbarOpen(true);
  };

  const formatDateForAPI = (date: Dayjs | null): string => {
    if (!date) return '';
    return date.format('YYYY-MM-DD');
  };

  // For Local Chapter Officer Election report, show only columns matching report_mode (lc vs spd).
  // Mode may be a comma-separated list (e.g. lc,reciprocity_lc) from column metadata.
  const effectiveColumns =
    id === REPORT_ID_LOCAL_CHAPTER_OFFICER_ELECTION && reportData?.report_mode
      ? (metadata?.columns?.filter((col) => {
          if (!col.mode) return true;
          const modes = col.mode.split(',').map((m) => m.trim());
          return modes.includes(reportData.report_mode!);
        }) ?? [])
      : (metadata?.columns ?? []);

  // Map report columns to ClientSideTable format
  const tableColumns: ClientSideTableColumn<any>[] = effectiveColumns.map(
    (col) => ({
      id: col.field,
      label: col.label,
      width: `${col.width}px`,
      sortable: col.sortable,
    })
  );

  // Prepare export columns (only include visible columns)
  const exportColumns = effectiveColumns
    .filter((col) => columnVisibilityModel[col.field] !== false)
    .map((col) => ({
      field: col.field,
      headerName: col.label,
      width: col.width,
    }));

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !metadata) {
    return (
      <Box sx={{ p: 3 }}>
        <AlertItem
          severity="error"
          description={
            error
              ? 'Failed to load report metadata. Please try again.'
              : 'Report not found.'
          }
          onClose={() => {}}
          keepOpen={true}
        />
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/peo-reports')}
        >
          Back to Reports
        </Button>
      </Box>
    );
  }

  const visibleReportParameters =
    id === REPORT_ID_MEMBERSHIP_YOY
      ? metadata.parameters.filter(
          (p) => p.name !== 'membership_year' && p.name !== 'as_of_date'
        )
      : metadata.parameters;
  const hasParameterControls =
    visibleReportParameters.length > 0 || id === REPORT_ID_MEMBERSHIP_YOY;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3, pb: 8 }}>
        {/* Header */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <IconButton onClick={() => navigate('/peo-reports')}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              {metadata.name}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {metadata.description}
            </Typography>
          </Box>
        </Stack>

        {/* Filters Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Report Parameters
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {!hasParameterControls ? (
              <Typography variant="body2" color="text.secondary">
                This report does not require any parameters.
              </Typography>
            ) : (
              <Grid container spacing={3}>
                {id === REPORT_ID_MEMBERSHIP_YOY && (
                  <>
                    <Grid item xs={12}>
                      <FormLabel id="membership-yoy-mode-label" sx={{ mb: 1 }}>
                        Run report using
                      </FormLabel>
                      <RadioGroup
                        row
                        aria-labelledby="membership-yoy-mode-label"
                        value={membershipYoyMode}
                        onChange={(e) => {
                          const v = e.target.value as MembershipYoyMode;
                          setMembershipYoyMode(v);
                          if (v === 'full_year') {
                            setFilterValues((prev) => {
                              const next: ExecuteReportRequest = { ...prev };
                              delete next.as_of_date;
                              if (
                                next.membership_year == null ||
                                !Number.isFinite(next.membership_year)
                              ) {
                                next.membership_year =
                                  getCurrentMembershipYear();
                              }
                              return next;
                            });
                          } else {
                            setFilterValues((prev) => {
                              const next: ExecuteReportRequest = { ...prev };
                              delete next.membership_year;
                              return next;
                            });
                          }
                        }}
                      >
                        <FormControlLabel
                          value="full_year"
                          control={<Radio size="small" />}
                          label="Full membership year"
                        />
                        <FormControlLabel
                          value="as_of_date"
                          control={<Radio size="small" />}
                          label="As of date (compare YTD to same point last year)"
                        />
                      </RadioGroup>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.5 }}
                      >
                        Choose one: either a complete membership year, or a
                        calendar date (through that day, UTC). Do not use both.
                      </Typography>
                    </Grid>
                    {membershipYoyMode === 'full_year' && (
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth size="small" required>
                          <InputLabel id="membership-yoy-year-label">
                            Membership year *
                          </InputLabel>
                          <Select
                            labelId="membership-yoy-year-label"
                            id="membership-yoy-year-select"
                            value={
                              filterValues.membership_year === undefined
                                ? ''
                                : String(filterValues.membership_year)
                            }
                            label="Membership year *"
                            onChange={(e) => {
                              const value = e.target.value;
                              handleFilterChange(
                                'membership_year',
                                value === '' ? undefined : Number(value)
                              );
                            }}
                          >
                            {Array.from(
                              {
                                length: getCurrentMembershipYear() - 2010 + 1,
                              },
                              (_, i) => getCurrentMembershipYear() - i
                            ).map((year) => {
                              const nextYear = (year + 1).toString().slice(-2);
                              return (
                                <MenuItem key={year} value={String(year)}>
                                  {year}-{nextYear}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                    {membershipYoyMode === 'as_of_date' && (
                      <Grid item xs={12} md={6}>
                        <DatePicker
                          label="As of date *"
                          value={
                            filterValues.as_of_date
                              ? dayjs(filterValues.as_of_date)
                              : null
                          }
                          onChange={(newValue) =>
                            handleFilterChange(
                              'as_of_date',
                              formatDateForAPI(newValue)
                            )
                          }
                          maxDate={dayjs()}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: 'small',
                              required: true,
                            },
                          }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          Must fall within a membership year and cannot be in
                          the future.
                        </Typography>
                      </Grid>
                    )}
                  </>
                )}
                {visibleReportParameters.map((param) => {
                  const isRequired = param.required;
                  const label = isRequired
                    ? `${param.description} *`
                    : param.description;
                  if (
                    id === REPORT_ID_RESOURCE_STATISTICS &&
                    param.name === 'library_category_id'
                  ) {
                    return (
                      <Grid item xs={12} md={6} key={param.name}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="rs-library-cat-label">
                            {label}
                          </InputLabel>
                          <Select
                            labelId="rs-library-cat-label"
                            value={filterValues.library_category_id ?? ''}
                            label={label}
                            onChange={(e) => {
                              const v = String(e.target.value);
                              setFilterValues((prev) => {
                                const next: ExecuteReportRequest = {
                                  ...prev,
                                  library_category_id: v,
                                };
                                const subId =
                                  prev.library_sub_category_id ?? '';
                                if (v && subId) {
                                  const sub = librarySubCategories.find(
                                    (s) => s.id === subId
                                  );
                                  if (sub && sub.category_id !== v) {
                                    next.library_sub_category_id = '';
                                  }
                                }
                                return next;
                              });
                            }}
                          >
                            <MenuItem value="">
                              <em>All</em>
                            </MenuItem>
                            {libraryCategories.map((c) => (
                              <MenuItem key={c.id} value={c.id}>
                                {c.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    );
                  }

                  if (
                    id === REPORT_ID_RESOURCE_STATISTICS &&
                    param.name === 'library_sub_category_id'
                  ) {
                    const catId = filterValues.library_category_id ?? '';
                    const subs = catId
                      ? librarySubCategories.filter(
                          (s) => s.category_id === catId
                        )
                      : librarySubCategories;
                    return (
                      <Grid item xs={12} md={6} key={param.name}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="rs-library-sub-label">
                            {label}
                          </InputLabel>
                          <Select
                            labelId="rs-library-sub-label"
                            value={filterValues.library_sub_category_id ?? ''}
                            label={label}
                            onChange={(e) =>
                              handleFilterChange(
                                'library_sub_category_id',
                                String(e.target.value)
                              )
                            }
                          >
                            <MenuItem value="">
                              <em>All</em>
                            </MenuItem>
                            {subs.map((s) => (
                              <MenuItem key={s.id} value={s.id}>
                                {s.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    );
                  }

                  if (param.name === 'local_chapter_id') {
                    return (
                      <Grid item xs={12} md={6} key={param.name}>
                        <ChapterSelector
                          chapter={filterValues.local_chapter_id || ''}
                          spd={filterValues.spd_chapter_id || null}
                          keyId={id!}
                          disabled={false}
                          handleChapterSelect={(value: string | any | null) => {
                            handleFilterChange('local_chapter_id', value || '');
                          }}
                        />
                      </Grid>
                    );
                  }

                  if (param.name === 'spd_chapter_id') {
                    return (
                      <Grid item xs={12} md={6} key={param.name}>
                        <SPDSelector
                          spd={filterValues.spd_chapter_id || ''}
                          keyId={id!}
                          reportId={id!}
                          disabled={false}
                          handleSpdSelect={(value: string | any | null) => {
                            handleFilterChange('spd_chapter_id', value || '');
                          }}
                        />
                      </Grid>
                    );
                  }

                  if (
                    id === REPORT_ID_LOCAL_CHAPTER_OFFICER_ELECTION &&
                    param.name === 'officer_election_type'
                  ) {
                    return (
                      <Grid item xs={12} sm={6} md={4} key={param.name}>
                        <FormControl
                          fullWidth
                          size="small"
                          required={isRequired}
                        >
                          <InputLabel id="officer-election-type-label">
                            Officer type
                          </InputLabel>
                          <Select
                            labelId="officer-election-type-label"
                            value={
                              filterValues.officer_election_type ??
                              'local_chapter'
                            }
                            label="Officer type"
                            onChange={(e) =>
                              handleFilterChange(
                                'officer_election_type',
                                e.target.value as string
                              )
                            }
                          >
                            <MenuItem value="local_chapter">
                              Local chapter (election of officers form)
                            </MenuItem>
                            <MenuItem value="spd_chapter">
                              SPD chapter (officers after convention)
                            </MenuItem>
                            <MenuItem value="reciprocity">
                              Reciprocity (President / Treasurer / VP)
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    );
                  }

                  if (
                    id === REPORT_ID_LOCAL_CHAPTER_OFFICER_ELECTION &&
                    param.name === 'election_completion_filter'
                  ) {
                    return (
                      <Grid item xs={12} sm={6} md={4} key={param.name}>
                        <FormControl
                          fullWidth
                          size="small"
                          required={isRequired}
                        >
                          <InputLabel id="election-completion-filter-label">
                            Status
                          </InputLabel>
                          <Select
                            labelId="election-completion-filter-label"
                            value={
                              filterValues.election_completion_filter ?? 'all'
                            }
                            label="Status"
                            onChange={(e) =>
                              handleFilterChange(
                                'election_completion_filter',
                                e.target.value as string
                              )
                            }
                          >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                            <MenuItem value="not_completed">
                              Not completed
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    );
                  }

                  if (
                    (id === REPORT_ID_CHAPTER_LISTING ||
                      id === REPORT_ID_INCREASE_DECREASE_BY_CHAPTER) &&
                    param.name === 'chapter_status'
                  ) {
                    return (
                      <Grid item xs={12} sm={6} md={4} key={param.name}>
                        <FormControl
                          fullWidth
                          size="small"
                          required={isRequired}
                        >
                          <InputLabel id="chapter-status-label">
                            Chapter status
                          </InputLabel>
                          <Select
                            labelId="chapter-status-label"
                            value={filterValues.chapter_status ?? 'active'}
                            label="Chapter status"
                            onChange={(e) =>
                              handleFilterChange(
                                'chapter_status',
                                e.target.value as string
                              )
                            }
                          >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="disbanded">Disbanded</MenuItem>
                            <MenuItem value="merged">Merged</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    );
                  }

                  if (
                    id === REPORT_ID_YEARS_OF_MEMBERSHIP_SPD &&
                    param.name === 'membership_year_target'
                  ) {
                    return (
                      <Grid item xs={12} sm={6} md={4} key={param.name}>
                        <FormControl
                          fullWidth
                          size="small"
                          required={isRequired}
                        >
                          <InputLabel id="yom-my-label">
                            Membership year (anniversary as-of) *
                          </InputLabel>
                          <Select
                            labelId="yom-my-label"
                            value={
                              filterValues.membership_year_target ?? 'current'
                            }
                            label="Membership year (anniversary as-of) *"
                            onChange={(e) =>
                              handleFilterChange(
                                'membership_year_target',
                                e.target.value as 'current' | 'next'
                              )
                            }
                          >
                            <MenuItem value="current">
                              Current membership year
                            </MenuItem>
                            <MenuItem value="next">
                              Next membership year
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    );
                  }

                  if (
                    id === REPORT_ID_YEARS_OF_MEMBERSHIP_SPD &&
                    param.name === 'anniversary_years'
                  ) {
                    const yomSlugs = [
                      '5',
                      '10',
                      '15',
                      '20',
                      '25',
                      '30',
                      '35',
                      '40',
                      '45',
                      '50',
                      '55',
                      '60',
                      '65',
                      '70_plus',
                    ];
                    const selected = (filterValues.anniversary_years ?? '')
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean);
                    return (
                      <Grid item xs={12} key={param.name}>
                        <FormControl
                          fullWidth
                          size="small"
                          required={isRequired}
                        >
                          <InputLabel id="yom-ann-label">
                            Anniversaries to include *
                          </InputLabel>
                          <Select
                            labelId="yom-ann-label"
                            multiple
                            value={selected}
                            onChange={(e) => {
                              const v = (
                                typeof e.target.value === 'string'
                                  ? e.target.value.split(',')
                                  : e.target.value
                              ) as string[];
                              if (v.length === 0) return;
                              handleFilterChange(
                                'anniversary_years',
                                v.join(',')
                              );
                            }}
                            input={
                              <OutlinedInput label="Anniversaries to include *" />
                            }
                            renderValue={(sel) =>
                              (sel as string[])
                                .map((s) =>
                                  s === '70_plus' ? '70+' : `${s} yr`
                                )
                                .join(', ')
                            }
                          >
                            {yomSlugs.map((slug) => (
                              <MenuItem key={slug} value={slug}>
                                <Checkbox
                                  checked={selected.includes(slug)}
                                  size="small"
                                />
                                {slug === '70_plus'
                                  ? '70 or more years'
                                  : `${slug} years`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          Members active in the selected S/P/D whose years since
                          initiation match any selected milestone (as of end of
                          the chosen membership year).
                        </Typography>
                      </Grid>
                    );
                  }

                  if (param.name === 'status') {
                    return (
                      <Grid item xs={12} sm={6} md={4} key={param.name}>
                        <MemberActiveFilter
                          filterValue={filterValues.status || ''}
                          updateFilters={(value: string) =>
                            handleFilterChange('status', value)
                          }
                          disabled={false}
                        />
                      </Grid>
                    );
                  }

                  if (param.name === 'local_chapter_roles') {
                    return (
                      <Grid item xs={12} md={6} key={param.name}>
                        <LocalChapterRolesFilter
                          value={filterValues.local_chapter_roles || ''}
                          onChange={(value: string) =>
                            handleFilterChange('local_chapter_roles', value)
                          }
                          disabled={false}
                          required={param.required}
                        />
                      </Grid>
                    );
                  }

                  if (param.name === 'position_group_type_ids') {
                    return (
                      <Grid item xs={12} md={6} key={param.name}>
                        <LeadershipPositionGroupsFilter
                          value={filterValues.position_group_type_ids || ''}
                          onChange={(value: string) =>
                            handleFilterChange('position_group_type_ids', value)
                          }
                          disabled={false}
                          required={param.required}
                          disabledOptionIds={
                            filterValues.organization_wide &&
                            metadata?.leadership_listing
                              ?.position_group_type_ids_disallowed_organization_wide
                              ? metadata.leadership_listing
                                  .position_group_type_ids_disallowed_organization_wide
                              : undefined
                          }
                        />
                      </Grid>
                    );
                  }

                  if (param.name === 'position_role_ids') {
                    return (
                      <Grid item xs={12} md={6} key={param.name}>
                        <LeadershipPositionsFilter
                          value={filterValues.position_role_ids || ''}
                          onChange={(value: string) =>
                            handleFilterChange('position_role_ids', value)
                          }
                          disabled={false}
                          required={param.required}
                        />
                      </Grid>
                    );
                  }

                  if (param.name === 'members') {
                    const membersLabel = isRequired
                      ? 'Member type *'
                      : 'Member type';
                    return (
                      <Grid item xs={12} sm={6} md={4} key={param.name}>
                        <FormControl
                          fullWidth
                          size="small"
                          required={isRequired}
                        >
                          <InputLabel id="members-label">
                            {membersLabel}
                          </InputLabel>
                          <Select
                            labelId="members-label"
                            id="members-select"
                            value={(filterValues.members ?? '') as string}
                            label={membersLabel}
                            onChange={(e) =>
                              handleFilterChange('members', e.target.value)
                            }
                          >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="inactive">Inactive</MenuItem>
                            <MenuItem value="psp">
                              Past S/P/D Presidents
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    );
                  }

                  if (param.name === 'only_disbanded') {
                    const onlyDisbandedLabel = isRequired
                      ? 'Only Disbanded? *'
                      : 'Only Disbanded?';
                    return (
                      <Grid item xs={12} sm={6} md={4} key={param.name}>
                        <FormControl
                          fullWidth
                          size="small"
                          required={isRequired}
                        >
                          <InputLabel id="only-disbanded-label">
                            {onlyDisbandedLabel}
                          </InputLabel>
                          <Select
                            labelId="only-disbanded-label"
                            id="only-disbanded-select"
                            value={
                              filterValues.only_disbanded === undefined
                                ? ''
                                : filterValues.only_disbanded === true
                                  ? 'true'
                                  : 'false'
                            }
                            label={onlyDisbandedLabel}
                            onChange={(e) => {
                              const value = e.target.value;
                              handleFilterChange(
                                'only_disbanded',
                                value === '' ? undefined : value === 'true'
                              );
                            }}
                          >
                            <MenuItem value="true">Yes</MenuItem>
                            <MenuItem value="false">No</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    );
                  }

                  if (
                    param.name === 'transaction_types' &&
                    id === REPORT_ID_CHAPTER_TRANSACTION_LOG
                  ) {
                    const selected = ctlSelectedSlugs(
                      filterValues.transaction_types
                    );
                    return (
                      <Grid item xs={12} key={param.name}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="ctl-types-label">
                            Transaction types
                          </InputLabel>
                          <Select
                            labelId="ctl-types-label"
                            id="ctl-types-select"
                            multiple
                            value={selected}
                            onChange={(e) => {
                              const v = (
                                typeof e.target.value === 'string'
                                  ? e.target.value.split(',')
                                  : e.target.value
                              ) as string[];
                              if (v.length === 0) return;
                              if (v.length === CTL_TRANSACTION_SLUGS.length) {
                                handleFilterChange('transaction_types', '');
                              } else {
                                handleFilterChange(
                                  'transaction_types',
                                  v.join(',')
                                );
                              }
                            }}
                            input={<OutlinedInput label="Transaction types" />}
                            renderValue={(selectedVals) =>
                              (selectedVals as string[])
                                .map(
                                  (s) =>
                                    CTL_TRANSACTION_LABELS[
                                      s as keyof typeof CTL_TRANSACTION_LABELS
                                    ] ?? s
                                )
                                .join(', ')
                            }
                          >
                            {CTL_TRANSACTION_SLUGS.map((slug) => (
                              <MenuItem key={slug} value={slug}>
                                <Checkbox
                                  checked={selected.includes(slug)}
                                  size="small"
                                />
                                {CTL_TRANSACTION_LABELS[slug]}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          All types included by default. Uncheck types to narrow
                          the report.
                        </Typography>
                      </Grid>
                    );
                  }

                  if (
                    param.name === 'include_member_contact' &&
                    id === REPORT_ID_CHAPTER_TRANSACTION_LOG
                  ) {
                    return (
                      <Grid item xs={12} key={param.name}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                filterValues.include_member_contact === true
                              }
                              onChange={(_, checked) =>
                                handleFilterChange(
                                  'include_member_contact',
                                  checked ? true : undefined
                                )
                              }
                            />
                          }
                          label="Include member contact (email, phones, address)"
                        />
                      </Grid>
                    );
                  }

                  if (param.name === 'years_ago_inactive') {
                    const yearsAgoLabel = isRequired
                      ? 'Inactive within (years) *'
                      : 'Inactive within (years)';
                    return (
                      <Grid item xs={12} sm={6} md={4} key={param.name}>
                        <YearsAgoFilter
                          value={filterValues.years_ago_inactive}
                          onChange={(v) =>
                            handleFilterChange('years_ago_inactive', v)
                          }
                          label={yearsAgoLabel}
                          required={isRequired}
                          allowNoLimit={true}
                        />
                      </Grid>
                    );
                  }

                  if (param.name === 'year') {
                    if (
                      id === REPORT_ID_IRS_LC_FULL_SUMMARY ||
                      id === REPORT_ID_IRS_RG_FULL_SUMMARY
                    ) {
                      const membershipYearLabel = isRequired
                        ? 'Membership year *'
                        : 'Membership year';
                      const maxEnd = getCurrentMembershipYear() + 1;
                      const minEnd = 2011;
                      const endYears = Array.from(
                        { length: maxEnd - minEnd + 1 },
                        (_, i) => maxEnd - i
                      );
                      return (
                        <Grid item xs={12} sm={6} md={4} key={param.name}>
                          <FormControl
                            fullWidth
                            size="small"
                            required={isRequired}
                          >
                            <InputLabel id="irs-full-summary-year-label">
                              {membershipYearLabel}
                            </InputLabel>
                            <Select
                              labelId="irs-full-summary-year-label"
                              id="irs-full-summary-year-select"
                              value={
                                filterValues.year === undefined ||
                                filterValues.year === null
                                  ? ''
                                  : String(filterValues.year)
                              }
                              label={membershipYearLabel}
                              onChange={(e) => {
                                const value = e.target.value;
                                handleFilterChange(
                                  'year',
                                  value === '' ? undefined : Number(value)
                                );
                              }}
                            >
                              {!isRequired && (
                                <MenuItem value="">
                                  <em>All years</em>
                                </MenuItem>
                              )}
                              {endYears.map((endYear) => {
                                const startYear = endYear - 1;
                                const nextTwo = endYear.toString().slice(-2);
                                return (
                                  <MenuItem
                                    key={endYear}
                                    value={String(endYear)}
                                  >
                                    {startYear}-{nextTwo}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                        </Grid>
                      );
                    }
                    if (id === REPORT_ID_FOUNDATION_FUNDS_ACTIVITY_STATEMENTS) {
                      const fiscalYearLabel = isRequired
                        ? 'Fiscal year *'
                        : 'Fiscal year';
                      const maxYear = getCurrentFiscalYearEnding();
                      const minYear = 2010;
                      const years = Array.from(
                        { length: maxYear - minYear + 1 },
                        (_, i) => maxYear - i
                      );
                      return (
                        <Grid item xs={12} sm={6} md={4} key={param.name}>
                          <FormControl
                            fullWidth
                            size="small"
                            required={isRequired}
                          >
                            <InputLabel id="foundation-fiscal-year-label">
                              {fiscalYearLabel}
                            </InputLabel>
                            <Select
                              labelId="foundation-fiscal-year-label"
                              value={
                                filterValues.year === undefined ||
                                filterValues.year === null
                                  ? ''
                                  : String(filterValues.year)
                              }
                              label={fiscalYearLabel}
                              onChange={(e) => {
                                const value = e.target.value;
                                handleFilterChange(
                                  'year',
                                  value === '' ? undefined : Number(value)
                                );
                              }}
                            >
                              {years.map((year) => {
                                const startYear = year - 1;
                                const nextTwo = year.toString().slice(-2);
                                return (
                                  <MenuItem key={year} value={String(year)}>
                                    {startYear}-{nextTwo} (July 1, {year})
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                        </Grid>
                      );
                    }
                    return (
                      <Grid item xs={12} sm={6} md={4} key={param.name}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label={label}
                          required={isRequired}
                          value={
                            filterValues.year === undefined ||
                            filterValues.year === null
                              ? ''
                              : String(filterValues.year)
                          }
                          onChange={(e) => {
                            const v = e.target.value;
                            handleFilterChange(
                              'year',
                              v === '' ? undefined : Number(v)
                            );
                          }}
                        />
                      </Grid>
                    );
                  }

                  if (
                    id === REPORT_ID_FOUNDATION_FUNDS_ACTIVITY_STATEMENTS &&
                    param.name === 'fund_type_id'
                  ) {
                    const fundTypeOptions = metadata.fund_type_options ?? [];
                    return (
                      <Grid item xs={12} sm={6} md={4} key={param.name}>
                        <FormControl
                          fullWidth
                          size="small"
                          required={isRequired}
                        >
                          <InputLabel id="fund-type-label">
                            Fund type *
                          </InputLabel>
                          <Select
                            labelId="fund-type-label"
                            value={filterValues.fund_type_id ?? ''}
                            label="Fund type *"
                            onChange={(e) =>
                              handleFilterChange(
                                'fund_type_id',
                                e.target.value as string
                              )
                            }
                          >
                            {fundTypeOptions.map((opt) => (
                              <MenuItem key={opt.id} value={opt.id}>
                                {opt.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    );
                  }

                  if (param.name === 'membership_year') {
                    const membershipYearLabel = isRequired
                      ? 'Membership Year *'
                      : 'Membership Year';
                    // Membership year starts March 1; max = current MY, min = 2010
                    const maxYear = getCurrentMembershipYear();
                    const minYear = 2010;
                    const years = Array.from(
                      { length: maxYear - minYear + 1 },
                      (_, i) => maxYear - i
                    );
                    const isIROC =
                      id === REPORT_ID_INTERNATIONAL_REP_OFFICERS_CONFERENCE;

                    return (
                      <Grid item xs={12} sm={6} md={4} key={param.name}>
                        <FormControl
                          fullWidth
                          size="small"
                          required={isRequired}
                        >
                          <InputLabel id="membership-year-label">
                            {membershipYearLabel}
                          </InputLabel>
                          <Select
                            labelId="membership-year-label"
                            id="membership-year-select"
                            value={
                              filterValues.membership_year === undefined
                                ? ''
                                : String(filterValues.membership_year)
                            }
                            label={membershipYearLabel}
                            onChange={(e) => {
                              const value = e.target.value;
                              handleFilterChange(
                                'membership_year',
                                value === '' ? undefined : Number(value)
                              );
                            }}
                          >
                            {isIROC && (
                              <MenuItem value="">
                                Current (as of today)
                              </MenuItem>
                            )}
                            {years.map((year) => {
                              const nextYear = (year + 1).toString().slice(-2); // Get last 2 digits
                              return (
                                <MenuItem key={year} value={String(year)}>
                                  {year}-{nextYear}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </Grid>
                    );
                  }

                  if (
                    id === REPORT_ID_LEADERSHIP_LISTING &&
                    param.name === 'organization_wide' &&
                    metadata?.is_international
                  ) {
                    return (
                      <Grid item xs={12} key={param.name}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(filterValues.organization_wide)}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setFilterValues((prev) => {
                                  if (!checked) {
                                    return {
                                      ...prev,
                                      organization_wide: false,
                                    };
                                  }
                                  const disallowed =
                                    metadata?.leadership_listing
                                      ?.position_group_type_ids_disallowed_organization_wide ??
                                    [];
                                  let pg = prev.position_group_type_ids ?? '';
                                  if (disallowed.length > 0 && pg) {
                                    const parts = pg
                                      .split(',')
                                      .map((s) => s.trim())
                                      .filter(
                                        (pid) => !disallowed.includes(pid)
                                      );
                                    pg = parts.join(',');
                                  }
                                  return {
                                    ...prev,
                                    organization_wide: true,
                                    local_chapter_id: undefined,
                                    spd_chapter_id: undefined,
                                    position_group_type_ids: pg || undefined,
                                  };
                                });
                              }}
                            />
                          }
                          label="Run for entire organization (International)"
                        />
                      </Grid>
                    );
                  }

                  if (
                    param.type === 'date' &&
                    (param.name === 'as_of_date' ||
                      param.name === 'start_date' ||
                      param.name === 'end_date')
                  ) {
                    // Use shorter labels for date fields
                    let dateLabel = label;
                    if (param.name === 'start_date') {
                      dateLabel = isRequired ? 'Start Date *' : 'Start Date';
                    } else if (param.name === 'end_date') {
                      dateLabel = isRequired ? 'End Date *' : 'End Date';
                    }
                    // as_of_date keeps the original label from param.description

                    return (
                      <Grid item xs={12} md={6} key={param.name}>
                        <DatePicker
                          label={dateLabel}
                          value={
                            filterValues[param.name]
                              ? dayjs(filterValues[param.name])
                              : null
                          }
                          onChange={(newValue) =>
                            handleFilterChange(
                              param.name,
                              formatDateForAPI(newValue)
                            )
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: 'small',
                              required: isRequired,
                            },
                          }}
                        />
                      </Grid>
                    );
                  }

                  return null;
                })}
              </Grid>
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {id !== REPORT_ID_INTERNATIONAL_REP_OFFICERS_CONFERENCE &&
                id !== REPORT_ID_MEMBERSHIP_GRAPHS &&
                id !== REPORT_ID_ANNUAL_REPORT_OF_MEMBERSHIP &&
                id !==
                  REPORT_ID_ANNUAL_REPORT_OF_MEMBERSHIP_DISBANDED_CHAPTERS &&
                id !== REPORT_ID_FUNDS_BY_SPD &&
                !ASYNC_PDF_REPORT_IDS.has(id ?? '') && (
                  <Button
                    variant="contained"
                    startIcon={<PlayArrowIcon />}
                    onClick={handleExecuteReport}
                    disabled={isExecuting || isRequiredFieldMissing()}
                  >
                    {isExecuting ? 'Running Report...' : 'Run Report'}
                  </Button>
                )}
              {id && PDF_DOWNLOAD_FROM_PARAMETERS_ONLY_IDS.has(id) && (
                <Button
                  variant="contained"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleDownloadPDF}
                  disabled={isPdfDownloading || isRequiredFieldMissing()}
                >
                  {isPdfDownloading ? 'Downloading PDF...' : 'Download PDF'}
                </Button>
              )}
              {id && ASYNC_PDF_REPORT_IDS.has(id) && (
                <Button
                  variant="contained"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleGenerateAsyncPDF}
                  disabled={isAsyncGenerating || isRequiredFieldMissing()}
                >
                  {isAsyncGenerating ? 'Generating PDF...' : 'Generate PDF'}
                </Button>
              )}
            </Box>

            {id && ASYNC_PDF_REPORT_IDS.has(id) && asyncError && (
              <Box sx={{ mt: 2 }}>
                <AlertItem
                  severity="error"
                  description={asyncError}
                  onClose={() => setAsyncError(null)}
                  keepOpen={true}
                />
              </Box>
            )}

            {id &&
              ASYNC_PDF_REPORT_IDS.has(id) &&
              isAsyncGenerating &&
              asyncStatus === 'processing' && (
                <Box
                  sx={{
                    mt: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Generating activity statements…
                    {asyncSummary?.printed_count != null
                      ? ` (${asyncSummary.printed_count} printed so far)`
                      : ''}
                  </Typography>
                </Box>
              )}

            {id && ASYNC_PDF_REPORT_IDS.has(id) && asyncDownloadURL && (
              <Box sx={{ mt: 2 }}>
                {asyncSummary && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Printed {asyncSummary.printed_count} statement
                    {asyncSummary.printed_count !== 1 ? 's' : ''}
                    {(asyncSummary.skipped?.length ?? 0) > 0
                      ? ` • Skipped ${asyncSummary.skipped!.length}`
                      : ''}
                    {(asyncSummary.excluded_multi_type?.length ?? 0) > 0
                      ? ` • Excluded ${asyncSummary.excluded_multi_type!.length} multi-type fund${asyncSummary.excluded_multi_type!.length !== 1 ? 's' : ''}`
                      : ''}
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  startIcon={<PictureAsPdfIcon />}
                  component="a"
                  href={asyncDownloadURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={asyncFilename ?? undefined}
                >
                  Download PDF
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Execution Error */}
        {executionError && (
          <Box sx={{ mb: 3 }}>
            <AlertItem
              severity="error"
              description={executionError}
              onClose={() => setExecutionError(null)}
              keepOpen={true}
            />
          </Box>
        )}

        {/* Results Card */}
        {reportData && (
          <Card>
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Report Results
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {reportData.row_count} record
                    {reportData.row_count !== 1 ? 's' : ''} • Executed in{' '}
                    {reportData.execution_time}ms
                  </Typography>
                </Box>
                <ExportButton
                  data={reportData.data || []}
                  columns={exportColumns}
                  filename={`${metadata.name.replace(/\s+/g, '_')}_${dayjs().format('YYYY-MM-DD')}`}
                  hasEmailColumn={hasEmailColumn}
                  onEmailExport={handleEmailExport}
                  excelOnly={id === REPORT_ID_RESOURCE_STATISTICS}
                  disableTableExport={
                    id === REPORT_ID_CHAPTER_STAR ||
                    id === REPORT_ID_INTERNATIONAL_REP_OFFICERS_CONFERENCE ||
                    id === REPORT_ID_MEMBERSHIP_GRAPHS ||
                    id === REPORT_ID_ANNUAL_REPORT_OF_MEMBERSHIP ||
                    id ===
                      REPORT_ID_ANNUAL_REPORT_OF_MEMBERSHIP_DISBANDED_CHAPTERS
                  }
                  hasPdfExport={reportSupportsPdfExport(id)}
                  onPdfExport={handleDownloadPDF}
                  isPdfDownloading={isPdfDownloading}
                  downloadButtonText="Download"
                />
              </Stack>

              <Divider sx={{ my: 2 }} />

              {!reportData.data || reportData.data.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No data found for the selected parameters.
                  </Typography>
                </Box>
              ) : id === REPORT_ID_CHAPTER_STAR &&
                Array.isArray(reportData.data) &&
                reportData.data.length > 0 &&
                reportData.data.every(
                  (row: unknown) =>
                    row != null &&
                    typeof row === 'object' &&
                    'year' in row &&
                    'active_count' in row
                ) ? (
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 2, textAlign: 'center' }}
                  >
                    Number of active members as of the end of each membership
                    year.
                  </Typography>
                  <LineChart
                    height={320}
                    series={[
                      {
                        data: (
                          reportData.data as {
                            year: number;
                            active_count: number;
                          }[]
                        ).map((d) => d.active_count),
                        label: 'Active count',
                      },
                    ]}
                    xAxis={[
                      {
                        scaleType: 'point',
                        data: (
                          reportData.data as {
                            year: number;
                            active_count: number;
                          }[]
                        ).map((d) => String(d.year)),
                      },
                    ]}
                    yAxis={[{ scaleType: 'linear', label: 'Active count' }]}
                    margin={{ top: 20, right: 30, bottom: 30, left: 50 }}
                  />
                </Box>
              ) : id === REPORT_ID_RESOURCE_STATISTICS &&
                Array.isArray(reportData.data) &&
                reportData.data.length > 0 ? (
                <Box sx={{ textAlign: 'center', py: 3, px: 2 }}>
                  <Typography variant="body1" color="text.secondary">
                    Data is ready. Use <strong>Download Excel</strong> above to
                    export results as an .xlsx file.
                  </Typography>
                </Box>
              ) : (
                <ClientSideTable
                  data={reportData.data}
                  columns={tableColumns}
                  enableColumnVisibility={true}
                  columnVisibilityModel={columnVisibilityModel}
                  onColumnVisibilityChange={setColumnVisibilityModel}
                  initialRowsPerPage={25}
                  rowsPerPageOptions={[10, 25, 50, 100, 250]}
                />
              )}
            </CardContent>
          </Card>
        )}

        {/* Leadership Listing: groups + positions union reminder */}
        <Dialog
          open={leadershipUnionDialogOpen}
          onClose={handleLeadershipUnionDialogCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Position groups and individual roles</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Making selections in both the Position Groups and Individual
              Positions drop-down boxes is only needed when generating a report
              for an entire board or committee (under Position Groups) with
              additional positions (under Individual Positions) that are not
              included within the board(s) or committee(s) selected. If an
              individual position is checked that is also included in the board
              or committee selected, the report will provide all members of the
              board or committee. To choose only certain positions within a
              board or committee, select the desired positions in the Individual
              Positions drop-down and do not choose anything under Position
              Groups.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={dontAskLeadershipUnionAgain}
                  onChange={(e) =>
                    setDontAskLeadershipUnionAgain(e.target.checked)
                  }
                />
              }
              label="Don't ask again this session"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleLeadershipUnionDialogCancel}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => void handleLeadershipUnionDialogConfirm()}
            >
              Run report
            </Button>
          </DialogActions>
        </Dialog>

        {/* Email Export Dialog */}
        <Dialog
          open={emailDialogOpen}
          onClose={() => setEmailDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Email List Export
            <IconButton
              onClick={() => setEmailDialogOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {emailCount} unique email address{emailCount !== 1 ? 'es' : ''}{' '}
              found. Emails are separated by semicolons for easy copy/paste into
              email clients.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={10}
              value={emailData}
              InputProps={{
                readOnly: true,
              }}
              sx={{ mb: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEmailDialogOpen(false)}>Close</Button>
            <Button
              variant="contained"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyEmails}
            >
              Copy to Clipboard
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default PEOReportDetail;
